import type { IFrameObject } from 'iframe-resizer'
import { iframeResizer } from 'iframe-resizer'

import {
  EMBED_AS,
  EMBED_MODE,
  JourneyEventType,
  LANGUAGE,
  type ContextData,
  type DataInjectionOptions,
  type EmbedChannel,
  type EmbeddingOptions
} from './types'
import { DEFAULT_JOURNEY_APP_BASE_URL, getWebComponentScriptUrl } from './utils/config'
import { isDebugMode } from './utils/debug'

/** iframe-resizer attaches its API to the iframe element under this key. */
type ResizableIframe = HTMLIFrameElement & { iFrameResizer?: IFrameObject }

/**
 * Fluent builder for embedding a Journey as either a native `<iframe>` or
 * the `<epilot-journey>` web component.
 *
 * Every configuration method returns `this`, so calls can be chained and
 * the chain ends with a placement method (`append`, `prepend`, `before`,
 * `after`) that inserts the element into the DOM.
 */
export class Embedding {
  // ─── Configuration ──────────────────────────────────────────────────────────

  /** Which Journey we're embedding. */
  protected $journeyId: string

  /**
   * Base URL of the Journey app — used for iframe `src` URLs and to derive
   * the `<epilot-journey>` web component script URL.
   * @default DEFAULT_JOURNEY_APP_BASE_URL ('https://journey.epilot.io')
   */
  protected $baseUrl: string

  /**
   * Explicit override for the `<epilot-journey>` web component script URL.
   * Takes precedence over `baseUrl + channel` when set. Primarily used in
   * local dev where the web component runs on a separate port.
   */
  protected $webComponentScriptUrl?: string

  /** Enables verbose iframe-resizer logging when true. */
  protected $debug: boolean

  /**
   * Embedding display mode (inline or full-screen).
   * @default EMBED_MODE.INLINE
   */
  protected $mode: EMBED_MODE = EMBED_MODE.INLINE

  /**
   * How the journey is rendered: as a native `<iframe>` or as the
   * `<epilot-journey>` web component.
   * @default EMBED_AS.WEB_COMPONENT
   */
  protected $component: EMBED_AS = EMBED_AS.WEB_COMPONENT

  /** Whether to show the journey top bar. */
  protected $topBar: boolean = true

  /** Whether to scroll the host page to the top on step navigation. */
  protected $scrollToTop: boolean = true

  /** Whether to show the close button inside the journey. */
  protected $closeButton: boolean = true

  /** Overrides the Journey App UI language. */
  protected $lang?: LANGUAGE

  /** Whether the journey is currently displayed in full-screen. */
  protected $isFullScreenEntered?: boolean

  /** Custom context data passed into the journey. */
  protected $contextData?: ContextData

  /** Data injection options (initial state, step index, display settings). */
  protected $dataInjectionOptions?: DataInjectionOptions

  /**
   * Accessible name for the embedded Journey.
   * - iframe: applied to both the `name` and `title` attributes.
   * - web component: applied to the host element's `title` attribute.
   */
  protected $name?: string

  /**
   * Identifier used for automated testing.
   * When set, added as a `data-testid` attribute on the created element.
   */
  protected $testId?: string

  /**
   * Optional `id` attribute for the embedded element.
   *
   * For the **iframe** backend this is read by `iframe-resizer` at init
   * time and used as its internal routing key — call `.id(value)` before
   * `.append()` / `.prepend()` / `.before()` / `.after()` to avoid
   * breaking auto-resize.
   */
  protected $id?: string

  /** Web component release channel used by `ensureWebComponentScript`. */
  protected $channel: EmbedChannel = 'stable'

  // ─── Runtime state ──────────────────────────────────────────────────────────

  /** Reference to the injected DOM element, set after a placement call. */
  protected $componentRef?: HTMLElement

  /**
   * Whether the iframe has completed its initial handshake (EPILOT/JOURNEY_LOADED).
   * Determines whether `enterFullScreen` can be posted immediately or must be deferred.
   */
  private $iframeReady = false

  /** Deferred fullscreen enter — flushed once the iframe fires `journeyIframeReady`. */
  private $pendingFullScreenEnter = false

  /** Cancels all `window` listeners registered by this embedding on `remove()`. */
  private $listenerController = new AbortController()

  // ─── Constructor ────────────────────────────────────────────────────────────

  constructor(journeyId: string, options: EmbeddingOptions = {}) {
    this.$journeyId = journeyId
    this.$baseUrl = options.baseUrl ?? DEFAULT_JOURNEY_APP_BASE_URL
    this.$webComponentScriptUrl = options.webComponentScriptUrl
    this.$debug = options.debug ?? false
  }

  // ─── Public: component type ─────────────────────────────────────────────────

  asIframe(): this {
    this.$component = EMBED_AS.IFRAME
    return this
  }

  asWebComponent(): this {
    this.$component = EMBED_AS.WEB_COMPONENT
    this.ensureWebComponentScript()
    return this
  }

  /**
   * Use the canary release channel for the web component script.
   * Call before `.asWebComponent()`, or after — if called after, the canary
   * script is loaded and replaces the stable one if it hasn't finished
   * registering the custom element yet.
   */
  canary(): this {
    this.$channel = 'canary'
    if (this.$component === EMBED_AS.WEB_COMPONENT) {
      this.ensureWebComponentScript()
    }
    return this
  }

  // ─── Public: configuration ──────────────────────────────────────────────────

  mode(mode: EMBED_MODE): this {
    this.$mode = mode
    this.$componentRef?.setAttribute('mode', mode)
    return this
  }

  topBar(value: boolean): this {
    this.$topBar = value
    return this
  }

  scrollToTop(value: boolean): this {
    this.$scrollToTop = value
    return this
  }

  closeButton(value: boolean): this {
    this.$closeButton = value
    return this
  }

  /**
   * Overrides the Journey App UI language.
   * @deprecated will be removed in future versions
   */
  lang(lang?: LANGUAGE): this {
    this.$lang = lang
    return this
  }

  contextData(data: ContextData): this {
    this.$contextData = data
    return this
  }

  dataInjectionOptions(options: DataInjectionOptions): this {
    this.$dataInjectionOptions = options
    return this
  }

  /**
   * Sets the accessible name for the embedded Journey.
   * Can be called before embedding (applied on creation) or after (updates the live element).
   *
   * - **iframe**: applied to both `name` and `title` attributes.
   * - **web component**: applied to the host element's `title` attribute.
   */
  name(value: string): this {
    this.$name = value

    if (this.$componentRef) {
      this.$componentRef.setAttribute('title', value)
      if (this.$component === EMBED_AS.IFRAME) {
        this.$componentRef.setAttribute('name', value)
      }
    }

    return this
  }

  testId(testId: string): this {
    this.$testId = testId
    return this
  }

  /**
   * Sets the `id` attribute on the embedded element.
   *
   * **For iframe embeds, call this BEFORE a placement method.**
   * `iframe-resizer` reads the iframe's id on initialisation and uses it
   * as its internal routing key for resize messages. Changing the id
   * after the iframe has loaded breaks auto-sizing. Web component embeds
   * are unaffected and can change the id at any time.
   */
  id(value: string): this {
    this.$id = value

    if (this.$componentRef) {
      this.$componentRef.setAttribute('id', value)

      if (this.$component === EMBED_AS.IFRAME && this.$iframeReady) {
        console.warn(
          '[epilot] .id() called after the iframe handshake completed. ' +
          'iframe-resizer may no longer route resize events to this iframe. ' +
          'Call .id(value) before the placement method to avoid this.'
        )
      }
    }

    return this
  }

  /**
   * Controls whether the journey is displayed in full-screen.
   * Can be called before embedding (sets initial state) or after (updates the live element).
   *
   * - **iframe**: manages the CSS fixed overlay, body scroll lock, and notifies the journey via postMessage.
   * - **web component**: updates the `is-full-screen-entered` attribute.
   */
  isFullScreenEntered(value: boolean): this {
    this.$isFullScreenEntered = value

    if (this.$componentRef) {
      if (this.$component === EMBED_AS.IFRAME) {
        this.updateIframeFullScreen(this.$componentRef as ResizableIframe, value)
      } else {
        this.$componentRef.setAttribute('is-full-screen-entered', String(value))
      }
    }

    return this
  }

  // ─── Public: DOM placement ──────────────────────────────────────────────────

  append(elementOrSelector: string | HTMLElement): this {
    const el = this.findElementOrThrow(elementOrSelector)
    const component = this.createComponent()

    el.append(component)
    this.$componentRef = component
    return this
  }

  prepend(elementOrSelector: string | HTMLElement): this {
    const el = this.findElementOrThrow(elementOrSelector)
    const component = this.createComponent()

    el.prepend(component)
    this.$componentRef = component
    return this
  }

  before(elementOrSelector: string | HTMLElement): this {
    const el = this.findElementOrThrow(elementOrSelector)
    const component = this.createComponent()

    el.parentNode?.insertBefore(component, el)
    this.$componentRef = component
    return this
  }

  after(elementOrSelector: string | HTMLElement): this {
    const el = this.findElementOrThrow(elementOrSelector)
    const component = this.createComponent()

    if (el.parentNode) {
      if (el.nextSibling) {
        el.parentNode.insertBefore(component, el.nextSibling)
      } else {
        el.parentNode.appendChild(component)
      }
    }

    this.$componentRef = component
    return this
  }

  // ─── Public: lifecycle ──────────────────────────────────────────────────────

  /** Returns the injected DOM element, or `null` if not yet placed. */
  el(): HTMLElement | null {
    return this.$componentRef ?? null
  }

  /** Removes the embedded element from the DOM and tears down all event listeners. */
  remove(): void {
    if (this.$componentRef) {
      // Web-component teardown triggers the journey app's internal MobX
      // cancellation flows, which surface as unhandled `CancelledError`
      // rejections. They're benign (the cancellation is working as
      // intended) but noisy. Suppress for a short window after removal.
      if (this.$component === EMBED_AS.WEB_COMPONENT) {
        this.suppressTeardownCancellations()
      }

      this.$listenerController.abort()
      this.$componentRef.remove()
      this.$componentRef = undefined
    }
  }

  /**
   * Installs a short-lived `unhandledrejection` filter that swallows
   * `CancelledError` rejections emitted by the journey app during
   * web-component disconnect. Scoped narrowly (by error shape) and
   * time-bounded so genuine cancellation errors elsewhere are not eaten.
   */
  private suppressTeardownCancellations(): void {
    if (typeof window === 'undefined') return

    const handler = (event: PromiseRejectionEvent) => {
      const reason = event.reason as { name?: string; message?: string } | undefined
      const isCancelled =
        reason?.name === 'CancelledError' ||
        (typeof reason?.message === 'string' &&
          reason.message.toLowerCase().includes('cancelled'))

      if (isCancelled) event.preventDefault()
    }

    window.addEventListener('unhandledrejection', handler)
    setTimeout(() => {
      window.removeEventListener('unhandledrejection', handler)
    }, 100)
  }

  // ─── Protected: component creation ──────────────────────────────────────────

  protected createComponent(): HTMLElement {
    if (this.$component === EMBED_AS.IFRAME) return this.createIframe()
    return this.createWebComponent()
  }

  protected createIframe(): HTMLElement {
    const iframe = document.createElement('iframe') as ResizableIframe
    const isFullScreen = this.$mode === EMBED_MODE.FULL_SCREEN
    const { signal } = this.$listenerController

    iframe.style.cssText = isFullScreen
      ? 'display:none; border:none'
      : 'display:block; border:none; width:100%; min-height:400px'

    const name = this.$name ?? 'epilot Journey'
    iframe.setAttribute('name', name)
    iframe.setAttribute('title', name)

    // Set id before iframe-resizer initialises so it uses our value as
    // its routing key instead of auto-assigning `iFrameResizer<N>`.
    if (this.$id) {
      iframe.setAttribute('id', this.$id)
    }

    if (this.$testId) {
      iframe.setAttribute('data-testid', this.$testId)
    }

    iframe.src = this.buildIframeUrl().toString()

    this.setupIframeHandshakeListener(iframe, signal, isFullScreen)
    this.setupIframeFullScreenListeners(signal)

    if (!isFullScreen) {
      this.setupIframeResizeListener(iframe, signal)
    }

    return iframe
  }

  protected createWebComponent(): HTMLElement {
    const element = document.createElement('epilot-journey')
    element.setAttribute('journey-id', this.$journeyId)
    element.setAttribute('mode', this.$mode)
    element.setAttribute('top-bar', String(this.$topBar))
    element.setAttribute('scroll-to-top', String(this.$scrollToTop))
    element.setAttribute('close-button', String(this.$closeButton))
    element.setAttribute('is-embedded', 'true')
    element.setAttribute('embed-source', 'sdk')

    if (this.$isFullScreenEntered !== undefined) {
      element.setAttribute('is-full-screen-entered', String(this.$isFullScreenEntered))
    }

    if (this.$lang) element.setAttribute('lang', this.$lang)
    if (this.$contextData) element.setAttribute('context-data', JSON.stringify(this.$contextData))
    if (this.$dataInjectionOptions) element.setAttribute('data-injection-options', JSON.stringify(this.$dataInjectionOptions))
    if (this.$name) element.setAttribute('title', this.$name)
    if (this.$testId) element.setAttribute('data-testid', this.$testId)
    if (this.$id) element.setAttribute('id', this.$id)

    return element
  }

  /**
   * Loads the web component script exactly once per page. Skips if the custom
   * element is already registered. Detects externally added scripts by URL match.
   */
  private ensureWebComponentScript(): void {
    if (typeof document === 'undefined') return
    if (customElements.get('epilot-journey')) return

    const scriptUrl = getWebComponentScriptUrl(this.$channel, this.$baseUrl, this.$webComponentScriptUrl)

    const externalScript = document.querySelector(`script[src="${scriptUrl}"]`)
    if (externalScript) return

    const existingScript = document.querySelector('script[data-epilot-embed]')
    if (existingScript) {
      if (existingScript.getAttribute('src') === scriptUrl) return
      existingScript.remove()
    }

    const script = document.createElement('script')
    script.src = scriptUrl
    script.type = 'module'
    script.async = true
    script.setAttribute('data-epilot-embed', '')
    document.head.appendChild(script)
  }

  // ─── Private: iframe listeners ──────────────────────────────────────────────

  /**
   * Waits for the journey's `EPILOT/JOURNEY_LOADED` handshake, then sends the
   * INIT payload and enables mode-specific behaviour (resizer or deferred fullscreen).
   */
  private setupIframeHandshakeListener(
    iframe: ResizableIframe,
    signal: AbortSignal,
    isFullScreen: boolean
  ) {
    window.addEventListener(
      'message',
      (event: MessageEvent) => {
        if (event?.data?.type !== JourneyEventType.journeyIframeReady) return

        // Error screens (e.g. nonce used) may resolve to a different journeyId
        // than the embed was configured with (journey-token override in the URL).
        // In that case match by iframe source instead so iframe-resizer still
        // initialises and the error screen can render at the correct height.
        const isErrorScreen = event.data?.payload?.isErrorScreen === true
        const matches = isErrorScreen
          ? event.source === iframe.contentWindow
          : event.data?.journeyId === this.$journeyId

        if (!matches) return

        const coercedContextData = this.getCoercedContextData()

        iframe.contentWindow?.postMessage(
          {
            type: JourneyEventType.init,
            status: 'effect',
            journeyId: this.$journeyId,
            payload: {
              mode: this.$mode,
              scrollToTop: this.$scrollToTop,
              closeButton: this.$closeButton,
              queryParams: this.getHostQueryParams(),
              ...(coercedContextData !== undefined && { contextData: coercedContextData }),
              initialMessageEventReceived: true
            }
          },
          '*'
        )

        this.$iframeReady = true

        if (isFullScreen) {
          if (this.$pendingFullScreenEnter) {
            this.$pendingFullScreenEnter = false
            this.postEnterFullScreen(iframe)
          }
        } else {
          this.initIframeResizer(iframe)
        }
      },
      { signal }
    )
  }

  /**
   * Pulses `iframeResizer.resize()` on every step navigation (`EPILOT/USER_EVENT/PAGE_VIEW`)
   * to handle async content height changes after the step renders.
   */
  private setupIframeResizeListener(iframe: ResizableIframe, signal: AbortSignal) {
    window.addEventListener(
      'message',
      (event: MessageEvent) => {
        if (
          event?.data?.type === JourneyEventType.pageView &&
          event.data?.journeyId === this.$journeyId
        ) {
          this.pulseResize(iframe)
        }
      },
      { signal }
    )
  }

  /**
   * Listens for fullscreen enter/exit events sent by the journey:
   * - `EPILOT/ENTER_FULLSCREEN` — launcher step navigated past; apply fixed overlay.
   * - `EPILOT/EXIT_FULLSCREEN` / `EPILOT/CLOSE_JOURNEY` — journey closed; restore previous style.
   *
   * Registered unconditionally to support both pure fullscreen and inline-to-fullscreen flows.
   */
  private setupIframeFullScreenListeners(signal: AbortSignal) {
    window.addEventListener(
      'message',
      (event: MessageEvent) => {
        if (event.data?.journeyId !== this.$journeyId) return

        if (event.data.type === JourneyEventType.enterFullScreen) {
          this.isFullScreenEntered(true)
        } else if (
          event.data.type === JourneyEventType.exitFullScreen ||
          event.data.type === JourneyEventType.closeJourney
        ) {
          this.isFullScreenEntered(false)
        }
      },
      { signal }
    )
  }

  // ─── Private: iframe-resizer ────────────────────────────────────────────────

  /**
   * Initialises iframe-resizer on an inline iframe using `taggedElement` height
   * calculation — matches the element inside the iframe with `data-iframe-height`.
   */
  private initIframeResizer(iframe: ResizableIframe) {
    iframeResizer(
      {
        log: this.$debug || isDebugMode(),
        heightCalculationMethod: 'taggedElement',
        checkOrigin: false,
      },
      iframe
    )
  }

  /**
   * Removes iframe-resizer listeners. Called before entering fullscreen to prevent
   * the resizer from fighting the fixed overlay.
   */
  private removeIframeResizer(iframe: ResizableIframe) {
    ;(iframe.iFrameResizer as unknown as { removeListeners?: () => void })?.removeListeners?.()
  }

  /**
   * Pulses `iframeResizer.resize()` every 500 ms for up to 5 seconds to handle
   * async content height changes after a step navigation.
   */
  private pulseResize(iframe: ResizableIframe) {
    const resizer = iframe.iFrameResizer
    if (!resizer) return

    let elapsed = 0
    const interval = setInterval(() => {
      resizer.resize()
      elapsed += 500
      if (elapsed >= 5000) clearInterval(interval)
    }, 500)
  }

  // ─── Private: fullscreen management ─────────────────────────────────────────

  /**
   * Applies or removes the full-screen fixed overlay on an iframe.
   * Saves and restores both the iframe style and the host page body overflow.
   */
  protected updateIframeFullScreen(iframe: ResizableIframe, value: boolean) {
    if (value) {
      iframe.dataset.prevStyle = iframe.style.cssText
      iframe.dataset.prevBodyOverflow = document.body.style.overflow

      this.removeIframeResizer(iframe)

      iframe.style.cssText = [
        'position:fixed',
        'top:0',
        'left:0',
        'width:100%',
        'height:100%',
        'border:none',
        'background-color:#fff',
        'overflow:auto',
        'z-index:9999999'
      ].join(';')

      document.body.style.overflow = 'hidden'

      if (this.$iframeReady) {
        this.postEnterFullScreen(iframe)
      } else {
        this.$pendingFullScreenEnter = true
      }
    } else {
      if (!iframe.dataset.prevStyle) return

      iframe.style.cssText = iframe.dataset.prevStyle
      document.body.style.overflow = iframe.dataset.prevBodyOverflow ?? ''

      delete iframe.dataset.prevStyle
      delete iframe.dataset.prevBodyOverflow

      if (this.$mode === EMBED_MODE.INLINE) {
        this.initIframeResizer(iframe)
      }

      this.postExitFullScreen(iframe)
    }
  }

  private postEnterFullScreen(iframe: ResizableIframe) {
    iframe.contentWindow?.postMessage(
      { type: JourneyEventType.enterFullScreen, status: 'success', journeyId: this.$journeyId, payload: {} },
      '*'
    )
  }

  private postExitFullScreen(iframe: ResizableIframe) {
    iframe.contentWindow?.postMessage(
      { type: JourneyEventType.exitFullScreen, status: 'success', journeyId: this.$journeyId, payload: {} },
      '*'
    )
  }

  // ─── Private: URL construction ──────────────────────────────────────────────

  protected buildIframeUrl(): URL {
    const url = new URL(this.$baseUrl)
    const queryParams = this.getHostQueryParams()

    url.searchParams.set('journeyId', this.$journeyId)
    url.searchParams.set('mode', this.$mode)
    url.searchParams.set('topBar', String(this.$topBar))
    url.searchParams.set('isEmbedded', 'true')
    url.searchParams.set('embedSource', 'sdk')
    url.searchParams.set('origin', this.getOrigin())

    if (this.$lang) {
      url.searchParams.set('lang', this.$lang)
    }

    if (this.$dataInjectionOptions) {
      url.searchParams.set(
        'dataInjectionOptions',
        window.btoa(encodeURIComponent(JSON.stringify(this.$dataInjectionOptions)))
      )
    }

    for (const [key, value] of Object.entries(queryParams)) {
      if (!url.searchParams.has(key)) {
        url.searchParams.set(key, value)
      }
    }

    const coercedContextData = this.getCoercedContextData()

    if (coercedContextData) {
      for (const [key, value] of Object.entries(coercedContextData)) {
        if (!url.searchParams.has(key)) {
          url.searchParams.set(key, value)
        }
      }
    }

    return url
  }

  /** Reads `origin` from contextData, defaulting to `EXTERNAL_WEBSITE`. */
  private getOrigin(): string {
    if (typeof this.$contextData?.origin === 'string') {
      return this.$contextData.origin
    }
    return 'EXTERNAL_WEBSITE'
  }

  /**
   * Coerces contextData values to strings for iframe transports (URL params
   * and INIT postMessage). Mirrors the `z.coerce.string()` behaviour the
   * journey app applies to the web-component `context-data` attribute.
   */
  private getCoercedContextData(): Record<string, string> | undefined {
    if (!this.$contextData) return undefined

    const coerced: Record<string, string> = {}
    for (const [key, value] of Object.entries(this.$contextData)) {
      if (typeof value === 'string' || typeof value === 'number') {
        coerced[key] = String(value)
      }
    }
    return coerced
  }

  /** Returns the host page's current URL query parameters for analytics forwarding (e.g. UTM). */
  private getHostQueryParams(): Record<string, string> {
    return Object.fromEntries(new URLSearchParams(window.location.search))
  }

  // ─── Protected: DOM utilities ───────────────────────────────────────────────

  protected findElement(elementOrSelector: string | HTMLElement): HTMLElement | null {
    if (typeof elementOrSelector === 'string') {
      return document.querySelector(elementOrSelector)
    }

    return document.body.contains(elementOrSelector) ? elementOrSelector : null
  }

  protected findElementOrThrow(elementOrSelector: string | HTMLElement): HTMLElement {
    const el = this.findElement(elementOrSelector)
    if (!el) throw new Error('Element not found in the DOM')
    return el
  }
}

export default Embedding
